export const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-surface-subtle px-4 py-12 font-sans sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl rounded-2xl border border-border-subtle bg-card px-10 py-12 shadow-sm">
        {/* Header */}
        <h1 className="mb-2 text-center text-3xl font-bold text-foreground">Privacy Policy</h1>
        <p className="mb-8 text-center text-sm font-semibold text-muted-foreground">
          Last updated: 06/24/2026
        </p>

        {/* Intro */}
        <p className="mb-4 leading-relaxed text-muted-foreground">
          Paribus LLC (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) is committed to
          protecting the privacy of visitors to our website and application. This Privacy Policy
          explains how we collect, use, disclose, and safeguard your information when you visit our
          site or use our services. Please read this privacy policy carefully. If you do not agree
          with the terms of this privacy policy, please do not access the site.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          This Privacy Policy applies to (i) visitors who browse any Paribus LLC website or
          sub-domain and (ii) registered users who access the Paribus LLC web application to upload,
          process, or review documents (including Protected Health Information). For users who
          handle Protected Health Information, this Policy operates in tandem with our Business
          Associate Agreement (&ldquo;BAA&rdquo;); if a term in this Policy conflicts with the BAA,
          the BAA governs for all PHI matters.
        </p>

        <hr className="my-8 border-border" />

        {/* Information We Collect */}
        <section>
          <h2 className="mb-4 text-xl font-bold text-foreground">Information We Collect</h2>
          <h3 className="mb-2 text-base font-semibold text-foreground">Personal Information</h3>
          <p className="mb-3 leading-relaxed text-muted-foreground">
            We may collect personal information that you provide directly to us, such as:
          </p>
          <ul className="mb-6 ml-2 list-inside list-disc space-y-1 text-muted-foreground">
            <li>Name </li>
            <li>Email address</li>
            <li>Firm name and address</li>
            <li>Areas of practice</li>
          </ul>
          <h3 className="mb-2 text-base font-semibold text-foreground">Non-Personal Information</h3>
          <p className="mb-3 leading-relaxed text-muted-foreground">
            We may also collect non-personal information about you automatically when you visit our
            site, including:
          </p>
          <ul className="ml-2 list-inside list-disc space-y-1 text-muted-foreground">
            <li>IP address</li>
            <li>Browser type</li>
            <li>Operating system</li>
            <li>Pages visited and time spent on those pages</li>
            <li>Referring website addresses</li>
          </ul>
        </section>

        <hr className="my-8 border-border" />

        {/* How We Use */}
        <section>
          <h2 className="mb-4 text-xl font-bold text-foreground">How We Use Your Information</h2>
          <p className="mb-3 leading-relaxed text-muted-foreground">
            We use the information we collect for various purposes, including to:
          </p>
          <ul className="ml-2 list-inside list-disc space-y-1 text-muted-foreground">
            <li>Provide, maintain, and improve our site and services</li>
            <li>Respond to your inquiries and fulfill your requests</li>
            <li>Send you administrative information, marketing communications, and newsletters</li>
            <li>Analyze usage patterns to enhance user experience</li>
            <li>Prevent fraudulent use of our services</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <hr className="my-8 border-border" />

        {/* Cookies */}
        <section>
          <h2 className="mb-4 text-xl font-bold text-foreground">Cookies</h2>
          <p className="leading-relaxed text-muted-foreground">
            We use (a) essential cookies so the site and login work, and (b) analytics cookies to
            understand usage and improve the Service. You can block non-essential cookies any time
            via your browser settings; doing so may affect site performance.
          </p>
        </section>

        <hr className="my-8 border-border" />

        {/* How We Share */}
        <section>
          <h2 className="mb-4 text-xl font-bold text-foreground">How We Share Your Information</h2>
          <p className="mb-3 leading-relaxed text-muted-foreground">
            We may share your information in the following situations:
          </p>
          <ul className="ml-2 list-inside list-disc space-y-1 text-muted-foreground">
            <li>With service providers who perform services on our behalf</li>
            <li>To comply with legal obligations</li>
            <li>To protect and defend our rights and property</li>
            <li>With your consent or at your direction</li>
          </ul>
        </section>

        <hr className="my-8 border-border" />

        {/* Data Security */}
        <section>
          <h2 className="mb-4 text-xl font-bold text-foreground">Data Security</h2>
          <p className="leading-relaxed text-muted-foreground">
            We implement appropriate technical and organizational measures to protect the
            information we collect and store. However, no method of transmission over the Internet
            or electronic storage is 100% secure, and we cannot guarantee absolute security.
          </p>
        </section>

        <hr className="my-8 border-border" />

        {/* Your Rights */}
        <section>
          <h2 className="mb-4 text-xl font-bold text-foreground">Your Rights</h2>
          <p className="mb-3 leading-relaxed text-muted-foreground">
            Depending on your location, you may have certain rights regarding your personal
            information, such as:
          </p>
          <ul className="ml-2 list-inside list-disc space-y-1 text-muted-foreground">
            <li>Access to your personal information</li>
            <li>Correction of inaccurate or incomplete information</li>
            <li>Deletion of your personal information</li>
            <li>Restriction of processing</li>
            <li>Data portability</li>
          </ul>
          <p className="mt-4 leading-relaxed text-muted-foreground">
            To exercise these rights, please contact us using the information provided in the
            &ldquo;Contact Us&rdquo; section below.
          </p>
        </section>

        <hr className="my-8 border-border" />

        {/* Third-Party Links */}
        <section>
          <h2 className="mb-4 text-xl font-bold text-foreground">Third-Party Links</h2>
          <p className="leading-relaxed text-muted-foreground">
            Our site may contain links to third-party websites. We have no control over and assume
            no responsibility for the content, privacy policies, or practices of any third-party
            sites or services.
          </p>
        </section>

        <hr className="my-8 border-border" />

        {/* Children's Privacy */}
        <section>
          <h2 className="mb-4 text-xl font-bold text-foreground">Children&apos;s Privacy</h2>
          <p className="leading-relaxed text-muted-foreground">
            Our site is not intended for children under the age of 18. We do not knowingly collect
            personal information from children under 18. If you are a parent or guardian and believe
            your child has provided us with personal information, please contact us.
          </p>
        </section>

        <hr className="my-8 border-border" />

        {/* Changes */}
        <section>
          <h2 className="mb-4 text-xl font-bold text-foreground">Changes to This Privacy Policy</h2>
          <p className="leading-relaxed text-muted-foreground">
            We may update our Privacy Policy from time to time. We will notify you of any changes by
            posting the new Privacy Policy on this page and updating the &ldquo;Last updated&rdquo;
            date.
          </p>
        </section>

        <hr className="my-8 border-border" />

        {/* Contact Us */}
        <section>
          <h2 className="mb-4 text-xl font-bold text-foreground">Contact Us</h2>
          <p className="mb-3 leading-relaxed text-muted-foreground">
            If you have any questions about this Privacy Policy, please contact us at:
          </p>
          <p className="font-semibold text-foreground">Paribus LLC</p>
          <p className="mt-1 text-muted-foreground">
            Email:{" "}
            <a href="mailto:legal@paribus.ai" className="text-primary hover:underline">
              legal@paribus.ai
            </a>
          </p>
        </section>
      </div>
    </div>
  );
};
